package org.matthewo.pages.event.tabs

import emotion.react.css
import org.matthewo.api.Playoffs
import org.matthewo.api.Schedule
import org.matthewo.api.Schedule.Alliance
import org.matthewo.bracket.*
import org.matthewo.pages.event.EventTabFC
import org.matthewo.routes.API
import org.matthewo.styles.*
import react.FC
import react.Props
import react.dom.html.ReactHTML.div
import tanstack.react.router.useNavigate
import web.cssom.*

val EventPlayoffs = EventTabFC<API.Event.Playoffs, Playoffs>({
    API.Event.Playoffs(it)
}) { (coroutineScope, year, eventCode, data) ->
    val navigate = useNavigate()

    val bracket = bracket(data?.alliances?.size) ?: return@EventTabFC

    val columns = [0.1.fr] + bracket.distinctBy { it.round }.flatMap { [1.fr, 0.15.fr] }.dropLast(1) + 0.1.fr

    fun columns(match: PlayoffMatch) = 2 * match.round..2 * match.round + 1

    val (dividers, rows) = bracket.createRows()

    div {
        css {
            flex = number(1.0)
            margin = 0.pct

            display = Display.grid

            gridTemplateColumns = array(*columns.toTypedArray())

            gridTemplateRows = array(*rows.toTypedArray())
        }

        val lineWidth = 2.px

        bracket.forEach { match ->
            val (red, blue) = [match.redFrom, match.blueFrom].map { if (it is AllianceFromMatch && it.winner) it.match else null }
            if (null == red && null == blue) return@forEach

            val cols = columns(match).first - 1..columns(match).last - 1
            val startRow = listOfNotNull(dividers.indices[match]?.last, dividers.indices[red]?.last).min()
            val endRow = listOfNotNull(dividers.indices[match]?.first, dividers.indices[blue]?.first).max()

            div {
                css {
                    gridColumns = cols
                    gridRows = dividers.indices[match]!!.first + 1..dividers.indices[match]!!.last + 1

                    display = Display.grid
                    gridTemplateColumns = array(1.fr, lineWidth, 1.fr)
                    gridTemplateRows = array(1.fr, lineWidth, 1.fr)
                }

                BracketConnector {
                    this.cols = 2..4
                    this.rows = 2..3
                }
            }

            if (startRow >= endRow) return@forEach

            div {
                css {
                    gridColumns = cols
                    gridRows = startRow + 1..endRow + 1

                    display = Display.grid
                    gridTemplateColumns = array(1.fr, lineWidth, 1.fr)
                    gridTemplateRows = array(1.fr)
                }

                BracketConnector {
                    this.cols = 2..3
                    this.rows = 1..2
                }
            }
        }

        fun childHorizontalLine(parent: PlayoffMatch, child: PlayoffMatch) {
            val cols = columns(child).last..<columns(parent).first
            val parentRows = dividers.indices[parent]!!.let { it.first + 1..it.last + 1 }
            val childRows = dividers.indices[child]!!.let { it.first + 1..it.last + 1 }

            val parentAboveChild = child.winnerTo.run { this !is AllianceToMatch || red }

            div {
                css {
                    gridColumns = cols.last..cols.last + 1
                    gridRows = childRows

                    display = Display.grid
                    gridTemplateColumns = array(1.fr, lineWidth, 1.fr)
                    gridTemplateRows = array(1.fr, lineWidth, 1.fr)
                }

                BracketConnector {
                    this.cols = 1..3
                    this.rows = 2..3
                }

                BracketConnector {
                    this.cols = 2..3
                    this.rows = if (parentAboveChild) 2..4 else 1..3
                }
            }

            div {
                css {
                    gridColumns = cols.last..cols.last + 1
                    gridRows = parentRows

                    display = Display.grid
                    gridTemplateColumns = array(1.fr, lineWidth, 1.fr)
                    gridTemplateRows = array(1.fr, lineWidth, 1.fr)
                }

                BracketConnector {
                    this.cols = 2..3
                    this.rows = if (parentAboveChild) 1..3 else 2..4
                }
            }

            if (cols.first >= cols.last) return
            div {
                css {
                    gridColumns = cols
                    gridRows = childRows

                    display = Display.grid
                    gridTemplateColumns = array(1.fr)
                    gridTemplateRows = array(1.fr, lineWidth, 1.fr)
                }

                BracketConnector {
                    this.cols = 1..2
                    this.rows = 2..3
                }
            }
        }

        bracket.forEach { match ->
            val red = match.redFrom
            if (red is AllianceFromMatch && red.winner) childHorizontalLine(match, red.match)
            val blue = match.blueFrom
            if (blue is AllianceFromMatch && blue.winner) childHorizontalLine(match, blue.match)
        }

        val results = (data?.matches ?: []).filter { it.level != Schedule.Match.Level.QUAL }.groupBy { it.series }.mapValues { it.value.maxByOrNull { it.number } }
        val alliances = (data?.alliances ?: []).associateBy { it.number }
        val finalNumber = bracket.maxOfOrNull { it.number } ?: Int.MAX_VALUE
        val tieBreaker = (results.keys.maxOrNull() ?: 0) > finalNumber
        bracket.forEach { match ->
            val matchNumber = match.number + if (tieBreaker && finalNumber == match.number) 1 else 0
            val matchData = results[matchNumber]
            div {
                css {
                    gridColumns = columns(match)
                    val row = dividers.indices[match]!!
                    gridRows = row.first + 1..row.last + 1

                    display = Display.grid
                    gridTemplateColumns = array(1.5.em, 1.fr)
                    gridTemplateRows = array(1.fr, 1.fr)

                    color = FONT_COLOR
                }

                div {
                    css {
                        position = Position.relative
                        top = (-1.5).em
                        gridRows = 1..1
                        gridColumns = 1..3
                        color = TITLE_FONT_COLOR
                    }
                    +"Match $matchNumber"
                }


                fun alliance(rows: IntRange, color: AllianceColor, from: AllianceSource<PlayoffMatch>, alliance: Alliance?, win: Boolean): List<Schedule.Team>? {
                    val (fromMatch, allianceNumberFrom) = when (from) {
                        is AllianceFromMatch -> {
                            val fromMatch = from.match
                            val r = results[fromMatch.number]
                            val red = r?.scores?.red
                            val blue = r?.scores?.blue
                            when {
                                red == null || blue == null -> fromMatch to null
                                (from.winner && red > blue) || (!from.winner && red < blue) -> fromMatch to r.red.number
                                (from.winner && red < blue) || (!from.winner && red > blue) -> fromMatch to r.blue.number
                                else -> fromMatch to null
                            }
                        }

                        is AllianceSelection -> null to from.number
                    }
                    val allianceFrom = alliances[allianceNumberFrom]
                    val teams = alliance?.teams ?: allianceFrom?.teams
                    div {
                        css {
                            gridColumns = 1..2
                            gridRows = rows
                            display = Display.flex
                            justifyContent = JustifyContent.center
                            alignItems = AlignItems.center
                            backgroundColor = color.highlightColor
                        }
                        +(alliance?.number ?: allianceFrom?.number ?: "").toString()
                    }
                    div {
                        css {
                            gridColumns = 2..3
                            gridRows = rows
                            backgroundColor = if (win) color.highlightColor else color.backgroundColor
                            display = Display.grid
                            gridAutoFlow = GridAutoFlow.column
                            gridAutoColumns = 1.fr
                        }
                        if (null != teams) {
                            teams.forEach { team ->
                                div {
                                    css {
                                        display = Display.flex
                                        alignItems = AlignItems.center
                                        padding = array(0.em, 0.5.em)
                                        overflow = Overflow.hidden
                                        whiteSpace = WhiteSpace.nowrap
                                    }
                                    +"${team.number}"
                                }
                            }
                        } else if (null != fromMatch) {
                            div {
                                css {
                                    display = Display.flex
                                    alignItems = AlignItems.center
                                    padding = array(0.em, 0.5.em)
                                    overflow = Overflow.hidden
                                    whiteSpace = WhiteSpace.nowrap
                                }
                                val progression = if (match.upper || !fromMatch.upper) "▲" else "▼"
                                +"M${fromMatch.number} $progression"
                            }
                        }
                    }
                    return teams
                }

                val redTeams = alliance(1..2, AllianceColor.RED, match.redFrom, matchData?.red, (matchData?.scores?.red ?: 0) > (matchData?.scores?.blue ?: 0))
                val blueTeams = alliance(2..3, AllianceColor.BLUE, match.blueFrom, matchData?.blue, (matchData?.scores?.blue ?: 0) > (matchData?.scores?.red ?: 0))

                onClick = {
                    navigate {
                        when {
                            null != matchData?.scores -> toEventMatchResult(match = matchData)
                            null != matchData -> toCompareTeams(match = matchData)
                            null != redTeams || null != blueTeams -> toCompareTeams(
                                red = redTeams?.map { it.number } ?: [],
                                blue = blueTeams?.map { it.number } ?: []
                            )
                        }
                    }
                }
            }
        }
    }
}


external interface BracketConnectorProps : Props {
    var rows: IntRange
    var cols: IntRange
}

private val BracketConnector = FC<BracketConnectorProps>("BracketConnector") { props ->
    div {
        css {
            gridColumns = props.cols
            gridRows = props.rows
            backgroundColor = FONT_COLOR
        }
    }
}
